import React from 'react';
import { useNavigate } from 'react-router-dom';
import RichTextDisplay from './RichTextDisplay';
import './CoinItem.css';

/**
 * CoinItem Component - Displays a coin transaction/recognition card
 *
 * @param {Object} props
 * @param {string|number} props.id - Unique identifier for the transaction
 * @param {string} props.coinType - Type of coin: 'individual' or 'organizational'
 * @param {string} props.senderName - Name of the person who sent the coin
 * @param {string} props.senderId - User ID of the sender
 * @param {string} props.message - The coin message/description
 * @param {string} props.timestamp - Formatted timestamp (e.g., "2 hours ago")
 * @param {string} [props.senderEmail] - Email of the sender (optional, shown as secondary text)
 * @param {string} [props.senderJobTitle] - Job title of the sender (optional, shown below name)
 * @param {boolean} [props.isPending] - Whether this transaction is pending
 * @param {boolean} [props.showPendingBadge] - Whether to show the pending badge
 * @param {string} [props.direction] - Transaction direction: 'received' or 'sent' (for styling)
 * @param {React.ReactNode} [props.additionalInfo] - Additional info to display at the bottom
 */
function CoinItem({
  id,
  coinType,
  senderName,
  senderId,
  message,
  timestamp,
  senderEmail,
  senderJobTitle,
  isPending = false,
  showPendingBadge = false,
  direction,
  additionalInfo
}) {
  const navigate = useNavigate();

  const handleUserClick = (e, userId) => {
    e.stopPropagation(); // Prevent triggering coin detail navigation
    if (userId) {
      navigate(`/profile/${userId}`);
    }
  };

  const handleCoinClick = () => {
    if (id) {
      navigate(`/transaction/${id}`);
    }
  };

  const handleCoinTypeBadgeClick = (e) => {
    e.stopPropagation(); // Prevent triggering coin detail navigation
    if (senderId) {
      // Navigate to sender's coins page with coin type filter
      navigate(`/profile/${senderId}/coins?type=${coinType}`);
    }
  };

  // Organization coins temporarily disabled - always show as Individual
  const coinTypeDisplay = 'Individual'; // coinType === 'individual' ? 'Individual' : 'Organizational';
  const coinIcon = '$'; // coinType === 'individual' ? '$' : '🏆';

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase();
  };

  return (
    <div
      className={`coin-item ${coinType} ${isPending ? 'pending' : ''} ${direction || ''} clickable-card`}
      onClick={handleCoinClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCoinClick();
        }
      }}
      title="View transaction details"
    >
      <div className="coin-item-header">
        <div
          className="coin-type-badge clickable"
          onClick={handleCoinTypeBadgeClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCoinTypeBadgeClick(e);
            }
          }}
          title={`View all ${coinTypeDisplay.toLowerCase()} coins from ${senderName}`}
        >
          <span className="badge-icon">{coinIcon}</span>
          <span className="badge-text">{coinTypeDisplay}</span>
        </div>
        {showPendingBadge && isPending && (
          <span className="pending-badge">⏳ Pending Request</span>
        )}
        <span className="coin-timestamp">{timestamp}</span>
      </div>

      <div className="coin-sender">
        <div
          className="sender-avatar clickable"
          onClick={(e) => handleUserClick(e, senderId)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleUserClick(e, senderId);
            }
          }}
          title={`View ${senderName}'s profile`}
        >
          {getInitials(senderName)}
        </div>
        <div className="sender-info">
          <div
            className="sender-name clickable"
            onClick={(e) => handleUserClick(e, senderId)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleUserClick(e, senderId);
              }
            }}
            title={`View ${senderName}'s profile`}
          >
            {senderName}
          </div>
          {senderJobTitle && (
            <div className="sender-job-title">{senderJobTitle}</div>
          )}
          {senderEmail && (
            <div className="sender-email">{senderEmail}</div>
          )}
        </div>
      </div>

      <div className="coin-message">
        <RichTextDisplay content={message} />
      </div>

      {additionalInfo && (
        <div className="coin-additional-info">
          {additionalInfo}
        </div>
      )}
    </div>
  );
}

export default CoinItem;
